import { useState, useCallback } from 'react';
import { ColumnDefinitionInput } from '../../components/table/types';
import { defaultInstagramColumns, INSTAGRAM_CREATOR_SEARCH_COLUMN_CONFIG_VERSION } from '../components/table/config/default-instagram-columns';

// Local storage management for column preferences
const COLUMN_PREFERENCES_KEY = 'aspire.creatorDiscovery.columnPreferences';

interface ColumnPreferences {
  version: number;
  columns: ColumnDefinitionInput[];
}

const getStoredColumnPreferences = (): ColumnDefinitionInput[] => {
  try {
    const stored = localStorage.getItem(COLUMN_PREFERENCES_KEY);
    if (!stored) return defaultInstagramColumns;

    const preferences: ColumnPreferences = JSON.parse(stored);

    // Check if stored version matches current version
    if (preferences.version !== INSTAGRAM_CREATOR_SEARCH_COLUMN_CONFIG_VERSION) {
      console.log('Column preferences version mismatch, using defaults');
      return defaultInstagramColumns;
    }

    return preferences.columns;
  } catch (error) {
    console.error('Error reading column preferences from localStorage:', error);
    return defaultInstagramColumns;
  }
};

const saveColumnPreferences = (columns: ColumnDefinitionInput[]): void => {
  try {
    const preferences: ColumnPreferences = {
      version: INSTAGRAM_CREATOR_SEARCH_COLUMN_CONFIG_VERSION,
      columns,
    };
    localStorage.setItem(COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving column preferences to localStorage:', error);
  }
};

export const useInstagramCreatorColumns = () => {
  const [selectedColumns, setSelectedColumns] = useState<ColumnDefinitionInput[]>(
    getStoredColumnPreferences()
  );

  const updateColumns = useCallback((columns: ColumnDefinitionInput[]) => {
    setSelectedColumns(columns);
    saveColumnPreferences(columns);
  }, []);

  const resetToDefaults = useCallback(() => {
    setSelectedColumns(defaultInstagramColumns);
    saveColumnPreferences(defaultInstagramColumns);
  }, []);

  return {
    selectedColumns,
    setSelectedColumns: updateColumns,
    resetToDefaults,
  };
};
